import { dictionaryApi } from './dictionaryClient';
import { type AdhikarLinkSutra, normalizeAnubrittiLinkSutra } from './ashtadhyayiApi';

export type BartikAdhikar = {
  id: number;
  ashtadhyayiBartikId: number;
  order: number;
  text: string;
  link: number | null;
  linkSutra: AdhikarLinkSutra | null;
};

export type CreateBartikAdhikarBody = {
  ashtadhyayiBartikId: number;
  order: number;
  text: string;
  link?: number | string | null;
  sutra?: number | string | null;
};

export type UpdateBartikAdhikarBody = Partial<Omit<CreateBartikAdhikarBody, 'ashtadhyayiBartikId'>>;

function isRecord(x: unknown): x is Record<string, unknown> {
  return x != null && typeof x === 'object' && !Array.isArray(x);
}

function unwrapEntityPayload(raw: unknown): unknown {
  if (isRecord(raw) && 'data' in raw) return raw.data;
  return raw;
}

function num(o: Record<string, unknown>, key: string): number | null {
  const v = o[key];
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}

function str(o: Record<string, unknown>, key: string): string {
  if (!(key in o) || o[key] == null) return '';
  return String(o[key]);
}

export function normalizeBartikAdhikar(raw: unknown): BartikAdhikar | null {
  if (!isRecord(raw)) return null;
  const id = num(raw, 'id');
  const ashtadhyayiBartikId = num(raw, 'ashtadhyayiBartikId') ?? num(raw, 'ashtadhyayi_bartik_id');
  const order = num(raw, 'order');
  if (id == null || ashtadhyayiBartikId == null || order == null) return null;
  const linkSutraRaw = raw.linkSutra ?? raw.link_sutra;
  const linkSutra = linkSutraRaw ? normalizeAnubrittiLinkSutra(linkSutraRaw) : null;
  const link = num(raw, 'link') ?? num(raw, 'sutra') ?? linkSutra?.id ?? null;
  return {
    id,
    ashtadhyayiBartikId,
    order,
    text: str(raw, 'text'),
    link,
    linkSutra,
  };
}

function normalizeList(data: unknown): BartikAdhikar[] {
  const rows =
    Array.isArray(data) ? data : isRecord(data) && Array.isArray(data.data) ? (data.data as unknown[]) : [];
  return rows
    .map(normalizeBartikAdhikar)
    .filter((x): x is BartikAdhikar => x != null)
    .sort((a, b) => a.order - b.order);
}

export async function listBartikAdhikar(ashtadhyayiBartikId?: number): Promise<BartikAdhikar[]> {
  const params =
    ashtadhyayiBartikId != null && Number.isFinite(ashtadhyayiBartikId)
      ? { ashtadhyayiBartikId }
      : undefined;
  const res = await dictionaryApi.get<unknown>('/ashtadhyayi-bartik-adhikar', { params });
  return normalizeList(res.data);
}

export async function getBartikAdhikar(id: number): Promise<BartikAdhikar> {
  const res = await dictionaryApi.get<unknown>(`/ashtadhyayi-bartik-adhikar/${id}`);
  const row = normalizeBartikAdhikar(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bartik adhikar response');
  return row;
}

export async function createBartikAdhikar(body: CreateBartikAdhikarBody): Promise<BartikAdhikar> {
  const res = await dictionaryApi.post<unknown>('/ashtadhyayi-bartik-adhikar', body);
  const row = normalizeBartikAdhikar(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bartik adhikar response');
  return row;
}

export async function updateBartikAdhikar(id: number, body: UpdateBartikAdhikarBody): Promise<BartikAdhikar> {
  const res = await dictionaryApi.patch<unknown>(`/ashtadhyayi-bartik-adhikar/${id}`, body);
  const row = normalizeBartikAdhikar(unwrapEntityPayload(res.data));
  if (!row) throw new Error('Invalid bartik adhikar response');
  return row;
}

export async function deleteBartikAdhikar(id: number): Promise<void> {
  await dictionaryApi.delete(`/ashtadhyayi-bartik-adhikar/${id}`);
}
